import { getFunctions, httpsCallable } from "firebase/functions";

/**
 * Client for `dspyroute_lintprompt`.
 *
 * The lint rules live on the backend (functions/src/llm/dspyRoute/negationLint)
 * because the DSPy route applies the same rules when it composes a prompt.
 * Keeping one copy means the warning an admin sees while writing is the warning
 * the route would log at request time — a second implementation here would
 * drift the first time either side is tuned.
 */

const functions = getFunctions();

const lintPromptCallable = httpsCallable(functions, "dspyroute_lintprompt", {
  timeout: 30000,
});

export const DSPY_ROUTE_DEFAULTS = {
  enabled: false,
  negation: { enabled: true, level: "focused" },
  authority: { enabled: true, level: "standard" },
};

export const NEGATION_LEVELS = [
  {
    value: "focused",
    label: "Focused",
    description:
      "Injects the five constructions the benchmark showed models failing. Cheapest.",
  },
  {
    value: "full",
    label: "Full",
    description:
      "Injects the whole negation taxonomy, including constructions we did not measure.",
  },
];

export const AUTHORITY_LEVELS = [
  {
    value: "standard",
    label: "Standard",
    description:
      "States the precedence order, then lists the organization rules as numbered mandatory items.",
  },
  {
    value: "strict",
    label: "Strict",
    description:
      "Adds a compliance check after the rules, at the very end of the prompt. Costs a few more tokens.",
  },
];

/**
 * Lint prompt text for negation constructions models resolve badly.
 *
 * @param {object} params
 * @param {string} params.text
 * @param {string[]} [params.disabledRules]
 * @returns {Promise<{ok: boolean, findings: object[], counts: object, structure: object}>}
 */
export async function lintPromptText({ text, disabledRules }) {
  const payload = { text: typeof text === "string" ? text : "" };
  if (Array.isArray(disabledRules) && disabledRules.length) {
    payload.disabledRules = disabledRules;
  }

  const result = await lintPromptCallable(payload);
  const data = result?.data;
  if (!data || data.status !== "ok") {
    throw new Error("Prompt lint returned no result.");
  }
  return data;
}

/** MUI `Alert` severity for a lint finding. */
export const severityForFinding = (finding) => {
  if (finding?.severity === "error") return "error";
  if (finding?.severity === "warning") return "warning";
  return "info";
};

export default { lintPromptText, DSPY_ROUTE_DEFAULTS };
